import * as React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from "@modules/datasheets/components/result/controllers/datasheet-element";
import { stringHasInteger } from "@common/utils/string";
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable } from '../../context';

export type NumberFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;

export const useNumberFieldController = (props: NumberFieldProps) => {
  const { datasheetElement, onChange } = props;
  // Use field.name if explicitly set (from user edit or undo restore), otherwise displayValue or field.value
  // Same pattern as text/date fields so undo/redo restores correctly
  const fieldName = (datasheetElement.original.field as any).name;
  const displayValue = (datasheetElement.original as any).displayValue ?? '';
  const fieldValueFromData = (datasheetElement.original.field as any).value;
  const valueFromProps = fieldName !== undefined
    ? String(fieldName)
    : String(displayValue ?? fieldValueFromData ?? '');
  const [fieldValue, setFieldValue] = React.useState(valueFromProps);

  React.useEffect(() => {
    // Keep local state in sync when the backing datasheet element changes
    // (e.g. after undo/redo or tab reload), but do NOT fight the user's
    // typing. Only react to upstream prop changes.
    const next = fieldName !== undefined
      ? String(fieldName)
      : String(displayValue ?? fieldValueFromData ?? '');
    if (fieldValue !== next) {
      setFieldValue(next);
    }
  }, [fieldName, displayValue, fieldValueFromData]);

  const { 
    headingSizeDependencies, 
    headingPlacedHorizontally, 
    headingPosition 
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const elementId = datasheetElement.id;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isEditable = useDatasheetResultFieldEditable(datasheetElement.original.editable !== false);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(elementId, { focus: () => inputRef.current?.focus() });
    return () => { registerField(elementId, null); };
  }, [elementId, registerField, isEditable]);

  const handleTabNavigation = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(elementId);
      } else {
        focusNext(elementId);
      }
    },
    [elementId, focusNext, focusPrev],
  );

  const handleFieldValueChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (stringHasInteger(e.target.value)) {
      setFieldValue(e.target.value);
    }
  }, []);

  const handleFieldValueSave = React.useCallback(() => {
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        displayValue: fieldValue,
        field: {
          ...datasheetElement.original.field,
          name: fieldValue,
          value: fieldValue,
        },
      },
    });
  }, [onChange, fieldValue, datasheetElement]);

  return {
    handleFieldValueSave,
    handleFieldValueChange,
    headingPosition,
    headingPlacedHorizontally,
    fieldValue,
    inputRef,
    handleTabNavigation,
    isEditable,
    ...baseDatasheetElementController,
  };
};